import { Component, OnDestroy, OnInit } from "@angular/core"
import { NgSwitch, NgClass, NgSwitchCase, AsyncPipe } from "@angular/common";
import { ToastService } from "src/app/services"
import { Observable, Subscription } from "rxjs"
import {
    Column,
    ContentType,
    CreateEmptyAverageMonthlyIncomeErrorsObject,
    CreateAverageMonthlyIncomeObject,
    ModifyEmptyAverageMonthlyIncomeErrorsObject,
    ModifyAverageMonthlyIncome,
    ModifyAverageMonthlyIncomeObject,
    setErrors,
} from "../../models"
import { ContextService, LoadingService } from "../../services"
import { HeaderComponent, TableComponent, TextInputComponent, NumberInputComponent } from "../../components";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
const CROATIA_COUNTRY_CODE = "hr"

@Component({
    selector: "valben-average-monthly-income",
    templateUrl: "./average-monthly-income.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        MatProgressSpinnerModule,
        HeaderComponent,
        NgSwitch,
        NgClass,
        NgSwitchCase,
        TableComponent,
        AsyncPipe,
        TextInputComponent,
        NumberInputComponent,
    ],
})
export class AverageMonthlyIncomePage implements OnInit, OnDestroy {
    tableValues: any[] = []
    resultSubscription: Subscription | undefined = new Subscription()
    itemsSubscription: Subscription | undefined = new Subscription()
    addItemObservable: () => Observable<any> | undefined = () =>
        this._contextService.getAddAvgMonthlyIncomeSubject()?.asObservable()
    editItemObservable: () => Observable<any> | undefined = () =>
        this._contextService.getEditAvgMonthlyIncomeSubject()?.asObservable()

    updateAddAverageMonthlyIncome: (value: number) => void
    updateEditAverageMonthlyIncome: (value: number) => void
    noopCountryCodeUpdate: (value: string) => void = () => {}

    createAvgMonthlyIncomeKeys = CreateAverageMonthlyIncomeObject()
    mofifyAvgMonthlyIncomeKeys = ModifyAverageMonthlyIncomeObject()
    currentEditItem: () => ModifyAverageMonthlyIncome

    addFormType: string = ""
    editFormType: string = ""
    avgMonthlyIncomeKey: string = ""

    loading$ = this._loadingService.avgMonthlyIncomeLoading()
    cErrors: { [key: string]: boolean } = CreateEmptyAverageMonthlyIncomeErrorsObject()
    mErrors: { [key: string]: boolean } = ModifyEmptyAverageMonthlyIncomeErrorsObject()
    refresh = () => this._contextService.resetAvgMonthlyIncome()

    cols: Column[] = [
        { field: "countryCode", header: "Šifra države", bodyType: "text", filterType: "text", filterMode: "contains" },
        { field: "income", header: "Prosječni mjesečni prihod (bruto 1)", bodyType: "number", sortable: true },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true },
    ]

    contentType: ContentType = ContentType.SEARCH
    editEnabled = false

    get hrRecordExists(): boolean {
        return this.tableValues.length > 0
    }

    setContentType(value: string) {
        switch (value) {
            case "Search":
                if (!(this.contentType === ContentType.SEARCH)) {
                    this.contentType = ContentType.SEARCH
                }
                break
            case "Add":
                if (!this.hrRecordExists && !(this.contentType === ContentType.ADD)) {
                    this.prepareAddForm()
                    this.contentType = ContentType.ADD
                }
                break
            case "Edit":
                if (!(this.contentType === ContentType.EDIT)) {
                    this.contentType = ContentType.EDIT
                }
                break
            default:
                if (!(this.contentType === ContentType.SEARCH)) {
                    this.contentType = ContentType.SEARCH
                }
                break
        }
    }

    constructor(
        private _messageService: ToastService,
        private _contextService: ContextService,
        private _loadingService: LoadingService,
    ) {
        this.addFormType = this._contextService.getAddKey()
        this.editFormType = this._contextService.getEditKey()
        this.avgMonthlyIncomeKey = this._contextService.getAvgMonthlyIncomeKey()

        this.updateAddAverageMonthlyIncome = (value: number) => {
            this._contextService.setItem(this.avgMonthlyIncomeKey, this.createAvgMonthlyIncomeKeys.Income, value, this.addFormType)
        }

        this.updateEditAverageMonthlyIncome = (value: number) => {
            this._contextService.setItem(this.avgMonthlyIncomeKey, this.mofifyAvgMonthlyIncomeKeys.Income, value, this.editFormType)
        }

        this.currentEditItem = () => this._contextService.getEditAvgMonthlyIncomeSubject()?.value
    }

    ngOnInit(): void {

        this._contextService.InitKngAvgMonthlyIncome()

        this.itemsSubscription = this._contextService.getAvgMonthlyIncomeSubject()?.subscribe(res => {
            this.tableValues = JSON.parse(JSON.stringify(res))
                .filter((x: { countryCode?: string }) => (x.countryCode ?? "").toLowerCase() === CROATIA_COUNTRY_CODE)
            this.tableValues.forEach(x => {
                x.createdDate = new Date(x.createdDate)
                x.modifiedDate = new Date(x.modifiedDate)
            })
        })
        this.resultSubscription = this._contextService.getAvgMonthlyIncomeResultSubject()?.subscribe(res => {
            if (res !== null) {
                if (res.success) {
                    if (res.formType === this.editFormType) {
                        this.editEnabled = false
                    } else {
                        TableComponent.resetPageOffset()
                    }
                    this.setContentType("Search")
                }
                res.formType === this.editFormType
                    ? setErrors(res.errorFields, this.mErrors)
                    : setErrors(res.errorFields, this.cErrors)
                this._messageService.add({
                    severity: res.error ? "error" : "success",
                    summary: res.error ? "Error" : "Success",
                    detail: res.message,
                    life: 10000,
                })
            }
        })
    }

    ngOnDestroy(): void {
        this.tableValues = []
        this.resultSubscription?.unsubscribe()
        this.itemsSubscription?.unsubscribe()
    }

    prepareAddForm() {
        this._contextService.resetAddAvgMonthlyIncome()
        this._contextService.setItem(
            this.avgMonthlyIncomeKey,
            this.createAvgMonthlyIncomeKeys.CountryCode,
            CROATIA_COUNTRY_CODE,
            this.addFormType,
        )
    }

    editOnClick(masterId: string) {
        this._contextService.setCurrentEditAvgMonthlyIncome(masterId)
        this.editEnabled = true
        this.setContentType("Edit")
    }

    save() {
        this._contextService.saveAvgMonthlyIncome()
    }

    modify() {
        this._contextService.modifyAvgMonthlyIncome()
    }

    backFromEdit() {
        this._contextService.setCurrentEditAvgMonthlyIncome(undefined)
        this.editEnabled = false
        this.setContentType("Search")
    }

    backFromAdd() {
        this._contextService.resetAddAvgMonthlyIncome()
        this.setContentType("Search")
    }
}
